import Flex from '@Components/Bases/Flex';
import FormCheckbox from '@Components/Form/FormCheckBox';
import ChangeTypeModal from '@Components/Modal/ChangeTypeModal';
import {
  useGetEsgDataPlatform,
  useSaveEsgDataPlatform,
} from '@Domains/Esg/EsgDataPlatform';
import {
  useGetEsgGreenhouse,
  useSaveEsgGreenhouse,
} from '@Domains/Esg/EsgGreenhouse';
import {
  ESG_PLAN,
  esgPlanOptions,
  EsgPlanOptionType,
  IEsgPlans,
  useGetEsgPlans,
  useSaveEsgPlans,
} from '@Domains/Esg/EsgPlan';
import { useDisclosure } from '@hooks/useDisclosure';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Snackbar,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { useProgressBar } from 'src/Constant/ProgressBarsContext/ProgressBar.Context';
import { EsgPlansFormValues } from '../EsgPlans';
import {
  getEmptyEsgDataPlatform,
  getEmptyGreenhouseData,
  mapEsgCharacteristic,
} from './utils';

const SelectEsgFormFields = () => {
  const [isDialogOpen, { open: openDialog, close: closeDialog }] =
    useDisclosure();
  const [openedSnackbar, { open: openSnackbar, close: closeSnackbar }] =
    useDisclosure();
  const { setValue, trigger, getValues, reset } =
    useFormContext<EsgPlansFormValues>();

  const typeChange = useRef<EsgPlanOptionType>('esgPlatform');
  const { id } = useParams();
  const [{ error }, saveEsgPlans] = useSaveEsgPlans(id);
  const [, saveEsgGreenhouse] = useSaveEsgGreenhouse(id);
  const [{ data: esgGreenhouseData }] = useGetEsgGreenhouse(id);
  const [, saveEsgDataPlatform] = useSaveEsgDataPlatform(id);
  const [{ data: esgDataPlatform }] = useGetEsgDataPlatform(id);
  const { refetchProgressWithUpdate, isPublished } = useProgressBar();

  const location = useLocation();
  const currentPath = location.pathname.split('/').pop();
  const [{ loading, error: errorGetEsgPlan }, refetch] = useGetEsgPlans(id);

  const refetchData = useCallback(async () => {
    try {
      const { data } = await refetch();
      if (data?.characteristicTypes?.length > 0) {
        reset(mapEsgCharacteristic(data.characteristicTypes));
      }
    } catch (err: unknown) {
      if (error instanceof Error && error.name !== 'CanceledError') {
        console.error('Failed to fetch esg-plans:', error);
      }
    }
  }, [refetch, reset, error]);

  useEffect(() => {
    if (currentPath === 'esg-plans') {
      refetchData();
    }
  }, [currentPath, refetchData]);

  if (loading) {
    return (
      <Flex justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress size={50} />
      </Flex>
    );
  }

  if (errorGetEsgPlan) {
    return (
      <Box p={2}>
        <Alert severity={'error'}>
          ไม่สามารถโหลดข้อมูลแผนการเปิดเผยข้อมูลแผนการยกระดับ
        </Alert>
      </Box>
    );
  }

  const getEsgPlanPayload = () => {
    const formValues = getValues();
    return [
      formValues.esgPlatform && ESG_PLAN.DATA_PLATFORM,
      formValues.esgGreenhouse && ESG_PLAN.GREEN_HOUSE,
    ].filter(Boolean);
  };

  const handleSaveEsgPlans = async () => {
    try {
      await saveEsgPlans({
        data: { characteristicTypes: getEsgPlanPayload() } as IEsgPlans,
      });

      await refetch();
    } catch (error) {
      console.error('Error saving ESG plans:', error);
      await refetch();
      openSnackbar();
    } finally {
      refetchProgressWithUpdate(true);
      openSnackbar();
    }
  };

  const handleSaveEsgDataPlatform = async () => {
    const payload = getEmptyEsgDataPlatform(esgDataPlatform);
    await saveEsgDataPlatform({ data: payload });
  };

  const handleSaveGreenhouseData = async () => {
    const payload = getEmptyGreenhouseData(esgGreenhouseData);
    await saveEsgGreenhouse({ data: { payload } });
  };

  const handleEsgPlanChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    option: EsgPlanOptionType
  ) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      await handleSaveEsgPlans();
    } else {
      typeChange.current = option;
      setValue(option, true);
      openDialog();
    }

    trigger();
  };

  const handleConfirmEsgPlanChange = async () => {
    setValue(typeChange.current, false);

    if (typeChange.current === 'esgPlatform') {
      await handleSaveEsgDataPlatform();
    } else if (typeChange.current === 'esgGreenhouse') {
      await handleSaveGreenhouseData();
    }

    await handleSaveEsgPlans();

    closeDialog();
  };

  return (
    <>
      <Box p={2} bgcolor={'white'}>
        <Typography fontWeight={800}>
          การเปิดเผยข้อมูลแผนการยกระดับ Environment, Social และ Governance
        </Typography>
        <Flex mt={2}>
          <InputLabel
            sx={{ textAlign: 'right', width: 1, maxWidth: 350 }}
            required
          >
            กรุณาเลือกว่าท่านต้องการเปิดเผยข้อมูลในลักษณะใด
          </InputLabel>
          <Box ml={3}>
            <FormControl>
              {esgPlanOptions.map((option) => (
                <FormCheckbox
                  key={option.value}
                  name={option.value}
                  label={option.label}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleEsgPlanChange(event, option.value);
                  }}
                  inputProps={{
                    disabled: isPublished,
                  }}
                />
              ))}
              {isDialogOpen && (
                <ChangeTypeModal
                  opened={isDialogOpen}
                  close={closeDialog}
                  confirm={handleConfirmEsgPlanChange}
                />
              )}
            </FormControl>
          </Box>
        </Flex>
      </Box>

      <Snackbar
        open={openedSnackbar}
        autoHideDuration={2000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={!!error ? 'error' : 'success'}
          onClose={closeSnackbar}
          sx={{
            alignItems: 'center',
            marginBottom: 6,
            '.MuiAlert-icon': { py: 1 },
            '.MuiAlert-message': { py: 1 },
          }}
        >
          {!!error ? 'เกิดข้อผิดพลาดในการบันทึก' : 'บันทึกสําเร็จ'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SelectEsgFormFields;
